import axios from 'axios';
import { clearAuth, getValidToken } from '../utils/auth';

const resolveApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL?.trim();

  // During local development without env config, keep localhost default.
  if (typeof window === 'undefined') {
    return envUrl || 'http://localhost:5005/api';
  }

  if (window.location.protocol === 'https:') {
    if (!envUrl) return '/api';

    try {
      const parsed = new URL(envUrl, window.location.origin);
      const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname);

      // Prevent mixed-content by routing insecure, non-local API URLs through same-origin HTTPS.
      if (parsed.protocol === 'http:' && !isLocalHost) {
        return '/api';
      }
    } catch (error) {
      return '/api';
    }
  }

  return envUrl || 'http://localhost:5005/api';
};

export const API_BASE_URL = resolveApiBaseUrl();

export const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = getValidToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isHandlingUnauthorized = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuth();

      if (typeof window !== 'undefined' && !isHandlingUnauthorized) {
        isHandlingUnauthorized = true;
        const isAuthPage = ['/login', '/register', '/forgot-password'].includes(window.location.pathname);

        if (!isAuthPage) {
          window.location.replace('/login');
        } else {
          isHandlingUnauthorized = false;
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  register: (name, email, password, username) => api.post('/auth/register', { name, email, password, username }),
  verify: (email, verificationCode) => api.post('/auth/verify', { email, verificationCode }),
  resendCode: (email) => api.post('/auth/resend-code', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, newPassword) => api.post('/auth/reset-password', { email, code, newPassword }),
};

// Invoice APIs
export const invoiceAPI = {
  getAll: (params = {}) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  updatePayment: (id, data) => api.put(`/invoices/${id}/payment`, data),
  sendReminder: (id, data) => api.post(`/invoices/${id}/send-reminder`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
};

export const projectAPI = {
  getAll: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
};

// Expense APIs
export const expenseAPI = {
  getAll: (params = {}) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  extractReceipt: (file) => {
    const formData = new FormData();
    formData.append('receipt', file);
    return api.post('/expenses/extract-receipt', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const recurringAPI = {
  getInvoices: () => api.get('/recurring/invoices'),
  createInvoice: (data) => api.post('/recurring/invoices', data),
  updateInvoice: (id, data) => api.put(`/recurring/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/recurring/invoices/${id}`),
  getExpenses: () => api.get('/recurring/expenses'),
  createExpense: (data) => api.post('/recurring/expenses', data),
  updateExpense: (id, data) => api.put(`/recurring/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/recurring/expenses/${id}`),
  getReminderLogs: () => api.get('/recurring/payment-reminders'),
};

// Customer APIs
export const customerAPI = {
  getAll: (params = {}) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Vendor APIs
export const vendorAPI = {
  getAll: (params = {}) => api.get('/vendors', { params }),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Report APIs
export const reportAPI = {
  getSummary: () => api.get('/reports/summary'),
  getMonthlyRevenue: () => api.get('/reports/monthly-revenue'),
  getExpenseBreakdown: () => api.get('/reports/expense-breakdown'),
};

// User APIs
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
};

export const companyProfileAPI = {
  get: () => api.get('/company-profile'),
  update: (data) => api.put('/company-profile', data),
};

// Estimates APIs
export const estimateAPI = {
  getAll: (params = {}) => api.get('/estimates', { params }),
  get: (id) => api.get(`/estimates/${id}`),
  create: (data) => api.post('/estimates', data),
  update: (id, data) => api.put(`/estimates/${id}`, data),
  delete: (id) => api.delete(`/estimates/${id}`),
  sendEmail: (id, data) => api.post(`/estimates/${id}/send-email`, data),
  accept: (id) => api.patch(`/estimates/${id}/accept`),
  // New endpoints for approval workflow, versioning, and PDF
  requestApproval: (id, data) => api.post(`/estimates/${id}/request-approval`, data),
  approve: (id) => api.post(`/estimates/${id}/approve`),
  reject: (id, data) => api.post(`/estimates/${id}/reject`, data),
  getVersions: (id) => api.get(`/estimates/${id}/versions`),
  downloadPdf: (id, data = {}) => api.post(`/estimates/${id}/download-pdf`, data, { responseType: 'blob' }),
};
